import { useState } from 'react';
import _ from 'lodash';
import { Avatar, Divider, Button, List, ListItem, ListItemIcon, Flex, Box, Text, Icon, IconButton, Tooltip, Heading } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Board, Alert, RemoveRound, Broadcast, MoveArrowRight, BoardPrivate, LearnMore, Delete } from '@vibe/icons';
import { BoardDiagnoseModal } from '@gorilla/ui/src/components/BoardDiagnoseModal';
import { type Slot } from '../services/portal';
import { mondayClient, monday } from '../services/monday';
import { BoardPickerModal } from '@gorilla/ui/src/components/BoardPickerModal';
import BoardName from '../shared/components/BoardName';
import { type BoardWithFolder, useAllBoards, useBoards } from '../shared/hooks/use-boards';

function parseJson(value: null | string) {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (err) {
    return null;
  }
}

function areStatusSettingsSame(templateStatusColumn: any, clientStatusColumn: any) {
  const templateStatusSettings = parseJson(templateStatusColumn.settings_str) || {};
  const clientStatusColumnSettings = parseJson(clientStatusColumn.settings_str) || {};

  return _.isEqual(templateStatusSettings.labels, clientStatusColumnSettings.labels);
}

function compareBoards(templateBoard: BoardWithFolder | undefined, clientBoard: BoardWithFolder | undefined) {
  const messages: string[] = [];

  if (!templateBoard) {
    messages.push('Template board or board source not found.');
  }

  if (!clientBoard) {
    messages.push('Clients Board not found.');
  }

  if (!templateBoard || !clientBoard) {
    return messages;
  }

  if (templateBoard.id === clientBoard.id) {
    return messages;
  }

  const clientBoardColumnsById = _.keyBy(clientBoard.columns, 'id');
  const clientBoardColumnsByTitle = _.keyBy(clientBoard.columns, 'title');

  for (const templateColumn of templateBoard.columns) {
    if (templateColumn.type === 'subtasks') {
      continue;
    }

    if (!clientBoardColumnsById[templateColumn.id]) {
      if (clientBoardColumnsByTitle[templateColumn.title] && clientBoardColumnsByTitle[templateColumn.title].type === templateColumn.type) {
        messages.push(
          `No column "${templateColumn.title}" found with ID "${templateColumn.id}". There is a column with the same title and type, but it has a different ID. If you have enabled mismatch handling in portal settings, the system will try to match columns by title and type.`,
        );
      } else {
        messages.push(`No column "${templateColumn.title}" found with ID "${templateColumn.id}".`);
      }

      continue;
    } else if (templateColumn.type !== clientBoardColumnsById[templateColumn.id].type) {
      messages.push(
        `Column "${templateColumn.title}" type differs. Expected "${templateColumn.type}", but got "${
          clientBoardColumnsById[templateColumn.id].type
        }."`,
      );
    } else if (templateColumn.type === 'status' && !areStatusSettingsSame(templateColumn, clientBoardColumnsById[templateColumn.id])) {
      messages.push(
        `The status settings of the Column "${templateColumn.title}" differ. This may lead to inconsistent behavior when the status column is used in filter settings.`,
      );
    }
  }

  return messages;
}

type SlotSettings = Record<string, { boardId: number }>;
type DeletedSlot = Slot & { isDeleted: true };

type SlotsProps = {
  slots: (Slot | DeletedSlot)[];
  slotSettings: SlotSettings;
  onChange: (slotSettings: SlotSettings) => void;
  isSaving: boolean;
};

export function Slots({ slots, slotSettings, onChange, isSaving }: SlotsProps) {
  const [boardPickerSlotId, setBoardPickerSlotId] = useState<string | null>(null);
  const [diagnoseBoardId, setDiagnoseBoardId] = useState<number | null>(null);
  //const [allSlotSettings, setAllSlotSettings] = useState<{ [key: string]: { boardId: number } }>({});
  const [deletionsInProgress, setDeletionsInProgress] = useState<string[]>([]);
  const settingsBoardIds = Object.values(slotSettings || {}).map((settings) => settings.boardId);
  const slotsTemplateBoardIds = (slots || []).map((slot) => slot.templateBoardId).filter((boardId) => boardId) as number[];
  const allBoardIds = _.uniq([...settingsBoardIds, ...slotsTemplateBoardIds]);
  const { boards, isLoading: areBoardsLoading } = useBoards(allBoardIds, false);
  const boardsById = _.keyBy(boards, 'id');
  const [errorModal, setErrorModal] = useState<string | false>(false);

  return (
    <>
      {diagnoseBoardId ? (
        <BoardDiagnoseModal
          boardId={diagnoseBoardId}
          mondayClient={mondayClient}
          onClose={() => {
            setDiagnoseBoardId(null);
          }}
        />
      ) : null}
      {boardPickerSlotId ? (
        <BoardPickerModal
          show={!!boardPickerSlotId}
          useBoards={useAllBoards}
          onClose={(boardId) => {
            const newSlotSettings = _.cloneDeep(slotSettings);

            if (boardId) {
              onChange({
                ...newSlotSettings,
                [boardPickerSlotId as string]: { boardId },
              });
            }

            setBoardPickerSlotId(null);
          }}
        />
      ) : null}
      {slots.length ? (
        <Box
          margin="large"
          border={true}
          borderColor="layoutBorderColor"
          rounded="small"
          backgroundColor="primaryBackgroundColor"
          shadow="xs"
        >
          <Flex gap={24} style={{ padding: '16px 16px 16px 20px' }}>
            <Flex gap={10}>
              <Icon icon={BoardPrivate} size={24} />
              <Heading type="h3" weight="bold" align="start" maxLines={1} ellipsis={false}>
                Dedicated boards
              </Heading>
              <Tooltip content="Assign this client’s dedicated board to a board setup below." position="right">
                <Icon icon={LearnMore} size={16} style={{ transform: 'translate3d(0,1px,0)' }} />
              </Tooltip>
            </Flex>
            <Box style={{ marginLeft: 'auto' }}>
              <Button
                kind={isSaving ? 'tertiary' : 'secondary'}
                size="small"
                loading={isSaving}
                leftFlat={isSaving}
                rightFlat={isSaving}
                noSidePadding={isSaving}
                style={{
                  pointerEvents: isSaving ? 'none' : 'auto',
                  paddingLeft: '13px',
                  paddingRight: '13px',
                }}
                onClick={() => {
                  monday.execute('openLinkInTab', {
                    url: 'https://getgorilla.app/products/client-portal/how-to-use/board-setup#show-each-client-their-own-dedicated-and-assigned-board',
                  });
                }}
              >
                {isSaving ? 'Saving' : 'Learn more'}
              </Button>
            </Box>
          </Flex>
          <List>
            {slots.map((slot, index) => {
              const settings = slotSettings[slot.slotId] || {};
              const compareMessages = compareBoards(
                slot.templateBoardId ? boardsById[slot.templateBoardId] : undefined,
                boardsById[settings.boardId],
              );

              return (
                <>
                  <Divider withoutMargin />
                  <ListItem
                    key={index}
                    className="portalmanager__content__listitem portalmanager__content__listitem--noclick"
                    size="medium"
                  >
                    {!areBoardsLoading && settings.boardId && compareMessages.length ? (
                      <Box style={{ marginRight: '16px' }}>
                        <Tooltip content="Show warnings" position="left">
                          <IconButton
                            icon={Alert}
                            size="small"
                            kind="primary"
                            color="negative"
                            onClick={() => {
                              setErrorModal(slot.slotId);
                            }}
                            disabled={isSaving}
                            className="custom__iconbutton--small"
                          />
                        </Tooltip>
                      </Box>
                    ) : null}

                    <Flex gap={10} style={{ flex: 1 }}>
                      <Flex gap={0}>
                        {slot.label === 'Deleted' ? (
                          <Flex
                            style={{
                              color: '#d83a52',
                            }}
                          >
                            <ListItemIcon icon={Broadcast} className="custom__listitemicon" />
                            <Text style={{ color: 'inherit' }}>Deleted</Text>
                          </Flex>
                        ) : (
                          <Flex>
                            <ListItemIcon icon={Broadcast} className="custom__listitemicon" />
                            <Text
                              ellipsis
                              style={{
                                //maxWidth: settings.boardId ? '140px' : '270px',
                                color: 'inherit',
                              }}
                            >
                              {slot.label}
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                      <Icon icon={MoveArrowRight} />
                      {settings.boardId ? (
                        <>
                          <Flex style={{ flex: 1 }}>
                            <ListItemIcon icon={Board} className="custom__listitemicon" />
                            <Text
                              ellipsis
                              style={{
                                maxWidth:
                                  !areBoardsLoading && settings.boardId && compareMessages.length
                                    ? 'calc(100vw - 400px)'
                                    : settings.boardId
                                      ? 'calc(100vw - 330px)'
                                      : 'auto',
                                color: 'inherit',
                              }}
                            >
                              {settings.boardId ? <BoardName boardId={settings.boardId} /> : ''}
                            </Text>
                          </Flex>
                          {/* 
                          {slot.label !== 'Deleted' ? (
                            <Button
                              kind="secondary"
                              size="xs"
                              disabled={isSaving}
                              onClick={() => {
                                setDiagnoseBoardId(settings.boardId);
                              }}
                            >
                              Diagnose
                            </Button>
                          ) : null}
                          */}
                        </>
                      ) : (
                        <Button
                          kind="primary"
                          size="xs"
                          //leftIcon={Board}
                          onClick={() => {
                            setBoardPickerSlotId(slot.slotId);
                          }}
                          disabled={isSaving}
                          style={{
                            marginLeft: '5px',
                          }}
                        >
                          Assign dedicated board
                        </Button>
                      )}
                    </Flex>

                    <Box style={{ marginLeft: 'auto' }}>
                      <Flex gap={0}>
                        {settings.boardId ? (
                          <>
                            <IconButton
                              icon={slot.label === 'Deleted' ? Delete : RemoveRound}
                              size="small"
                              kind="tertiary"
                              onClick={async () => {
                                setDeletionsInProgress([...deletionsInProgress, slot.slotId]);
                                try {
                                  await onChange(_.omit(slotSettings, slot.slotId));
                                } catch (err) {}
                                setDeletionsInProgress(deletionsInProgress.filter((id) => id !== slot.slotId));
                              }}
                              disabled={isSaving}
                              className="custom__iconbutton--small"
                            />
                          </>
                        ) : null}
                      </Flex>
                    </Box>

                    {errorModal === slot.slotId ? (
                      <AppModal
                        contentSpacing={false}
                        alertDialog={true}
                        data-testid="board-slot-errors-modal"
                        width={400}
                        show={errorModal === slot.slotId}
                        onClose={() => {
                          setErrorModal(false);
                        }}
                      >
                        <AppModalContent>
                          <Flex direction="row" align="center" gap={4} style={{ marginBottom: '24px' }}>
                            <Flex direction="row" align="center" gap={8}>
                              <Icon icon={Alert} size={24} />
                              <Heading type="h3" weight="bold" align="start">
                                Warnings
                              </Heading>
                            </Flex>
                          </Flex>
                          <Box
                            marginBottom="small"
                            padding="medium"
                            border={true}
                            rounded="small"
                            backgroundColor="allgreyBackgroundColor"
                            style={{
                              overflow: 'hidden',
                              overflowY: 'auto',
                              maxHeight: 'calc(100vh - 250px)',
                            }}
                          >
                            <Box>
                              <Text type="text3" element="ul" ellipsis={false} style={{ margin: 0, padding: 0, maxWidth: '500px' }}>
                                {compareMessages.map((message, index) => (
                                  <li
                                    key={index}
                                    style={{
                                      marginTop: index > 0 ? '8px' : '0',
                                    }}
                                  >
                                    <Flex gap={8} align="start">
                                      <Flex
                                        style={{
                                          transform: 'translate3d(0,1px,0)',
                                        }}
                                      >
                                        <Icon icon={MoveArrowRight} size={14} />
                                      </Flex>
                                      <div>{message}</div>
                                    </Flex>
                                  </li>
                                ))}
                              </Text>
                            </Box>
                          </Box>
                        </AppModalContent>
                      </AppModal>
                    ) : null}
                  </ListItem>
                </>
              );
            })}
          </List>
        </Box>
      ) : (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '24px',
            width: 'calc(100% - 50px)',
            height: 'calc(100vh - 123px)',
            minHeight: '300px',
            border: '1px dashed #D0D4E4',
            borderRadius: '4px',
          }}
        >
          <Flex
            direction="column"
            gap={16}
            style={{
              marginTop: '-32px',
            }}
          >
            <Flex direction="column" gap={4}>
              <Icon icon={Broadcast} size={32} />
              <Heading type="h3" weight="bold" align="center">
                Nothing to assign
              </Heading>
            </Flex>
            <Text type="text2" align="center" color="secondary">
              Create a board setup for dedicated boards <br />
              first, then assign one to this client here.
            </Text>
            <Button
              onClick={() => {
                monday.execute('openLinkInTab', {
                  url: 'https://getgorilla.app/products/client-portal/how-to-use/board-setup#show-each-client-their-own-dedicated-and-assigned-board',
                });
              }}
            >
              Learn more
            </Button>
          </Flex>
        </Box>
      )}
    </>
  );
}
